import {
    Scroll,
    ChatBubble,
    formatSecondsToTime,
    ComponentHeights,
} from '@tuhi/components'
import { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const Container = styled.div`
    width: 100%;
    height: 100%;
`

const TranscriptColumn = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    align-self: center;
    gap: 10px;

    > div:first-child {
        padding-top: 24px;
    }

    > div:last-child {
        padding-bottom: 24px;
    }
`

export interface TranscriptionBubbleContent {
    text: string
    speaker: number
    time: number
    endTime: number
}

interface Props {
    bubbles: TranscriptionBubbleContent[]
    focusedBubble?: number
    scrollToBottom?: boolean
    scrollViewOffset: ComponentHeights[]
    className?: string
}

export const TranscriptionViewer: FC<Props> = ({
    bubbles,
    focusedBubble,
    scrollToBottom = false,
    scrollViewOffset,
    className,
}) => {
    const ref = useRef<HTMLDivElement>(null)

    const activeBubble = () => {
        return scrollToBottom ? bubbles.length - 1 : focusedBubble
    }

    useEffect(() => {
        if (ref.current) {
            const chatBubbleToFocus = ref.current.querySelector<HTMLElement>(
                `#chat-bubble-${activeBubble()}`
            )
            chatBubbleToFocus?.scrollIntoView({ behavior: 'smooth' })
        }
    }, [bubbles, scrollToBottom, focusedBubble])

    return (
        <Container className={className}>
            <Scroll
                maxHeight="100vh"
                offsets={scrollViewOffset}
                verticalScrollBarVerticalPadding={14}
                verticalScrollBarLeftPadding={24}
            >
                <TranscriptColumn ref={ref}>
                    {bubbles.map((bubble, index) => {
                        const { speaker, text, time } = bubble
                        const alignment = speaker === 0 ? 'right' : 'left'
                        const startTime = formatSecondsToTime(Math.floor(time))

                        return (
                            <ChatBubble
                                active={index === activeBubble()}
                                index={index}
                                speaker={`${speaker}`}
                                key={index}
                                alignment={alignment}
                                time={startTime}
                                text={text}
                            />
                        )
                    })}
                </TranscriptColumn>
            </Scroll>
        </Container>
    )
}
